import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { FirebaseProvider, useFirebase } from './contexts/FirebaseContext';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { PartsSearch } from './pages/spare-shop/PartsSearch';
import { StockManagement } from './pages/spare-shop/StockManagement';
import { SupplierManagement } from './pages/spare-shop/SupplierManagement';
import { Analytics } from './pages/spare-shop/Analytics';
import { DealershipDashboard } from './pages/dealership/DealershipDashboard';
import { TransactionManagement } from './pages/dealership/TransactionManagement';
import { InventoryStatus } from './pages/dealership/InventoryStatus';
import { SupplierNetwork } from './pages/dealership/SupplierNetwork';
import { OrderTracking } from './pages/dealership/OrderTracking';
import { ServiceHistory as DealershipServiceHistory } from './pages/dealership/ServiceHistory';
import { CustomerManagement } from './pages/dealership/CustomerManagement';
import { ReportsAnalytics } from './pages/dealership/ReportsAnalytics';
import { ProfileManagement } from './pages/common/ProfileManagement';
import { MessagesHub } from './pages/common/MessagesHub';
import { NotificationsCenter } from './pages/common/NotificationsCenter';
import { PartsAlertsDashboard } from './pages/dealership/PartsAlertsDashboard';
import { CapacityManagement } from './pages/repair-shop/CapacityManagement';
import { RepairJobsBoard } from './pages/repair-shop/RepairJobsBoard';
import { PartsOrderInterface } from './pages/repair-shop/PartsOrderInterface';
import { ScheduleManagement } from './pages/repair-shop/ScheduleManagement';
import { ServiceHistory as RepairShopServiceHistory } from './pages/repair-shop/ServiceHistory';
import { AvailabilityCalendar } from './pages/repair-shop/AvailabilityCalendar';
import { SignUp } from './pages/SignUp';
import { ForgotPassword } from './pages/ForgotPassword';

// Add breakpoint mixins
const breakpoints = {
  xs: '@media (max-width: 600px)',
  sm: '@media (min-width: 601px) and (max-width: 960px)',
  md: '@media (min-width: 961px) and (max-width: 1280px)',
  lg: '@media (min-width: 1281px)',
};

// Create a theme instance
let theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFD700',
      light: '#FFE44D',
      dark: '#B39700',
      contrastText: '#000000',
    },
    secondary: {
      main: '#1A1A1A',
      light: '#2C2C2C',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0A0A0A',
      paper: '#1A1A1A',
    },
    error: {
      main: '#FF3D3D',
      light: '#FF5252',
      dark: '#D32F2F',
    },
    warning: {
      main: '#FFB800',
      light: '#FFC400',
      dark: '#FF9100',
    },
    success: {
      main: '#00C853',
      light: '#69F0AE',
      dark: '#00E676',
    },
    info: {
      main: '#0288D1',
      light: '#29B6F6',
      dark: '#0277BD',
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  typography: {
    fontFamily: "'Rajdhani', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontWeight: 600,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      background: 'linear-gradient(45deg, #FFD700 10%, #FFF 90%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      [breakpoints.xs]: {
        fontSize: '2rem',
      },
      [breakpoints.sm]: {
        fontSize: '2.5rem',
      },
      [breakpoints.md]: {
        fontSize: '3rem',
      },
      [breakpoints.lg]: {
        fontSize: '3.5rem',
      },
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '0.02em',
      background: 'linear-gradient(45deg, #FFD700 10%, #FFF 90%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      [breakpoints.xs]: {
        fontSize: '1.75rem',
      },
      [breakpoints.sm]: {
        fontSize: '2rem',
      },
      [breakpoints.md]: {
        fontSize: '2.5rem',
      },
      [breakpoints.lg]: {
        fontSize: '3rem',
      },
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '0.02em',
      [breakpoints.xs]: {
        fontSize: '1.5rem',
      },
      [breakpoints.sm]: {
        fontSize: '1.75rem',
      },
      [breakpoints.md]: {
        fontSize: '2rem',
      },
      [breakpoints.lg]: {
        fontSize: '2.5rem',
      },
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '0.02em',
      [breakpoints.xs]: {
        fontSize: '1.25rem',
      },
      [breakpoints.sm]: {
        fontSize: '1.5rem',
      },
      [breakpoints.md]: {
        fontSize: '1.75rem',
      },
      [breakpoints.lg]: {
        fontSize: '2rem',
      },
    },
    h5: {
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    h6: {
      fontWeight: 500,
      letterSpacing: '0.02em',
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@keyframes glow': {
          '0%': {
            boxShadow: '0 0 5px rgba(255, 215, 0, 0.2)',
          },
          '50%': {
            boxShadow: '0 0 20px rgba(255, 215, 0, 0.4)',
          },
          '100%': {
            boxShadow: '0 0 5px rgba(255, 215, 0, 0.2)',
          },
        },
        '@keyframes shine': {
          '0%': {
            backgroundPosition: '0% 50%',
          },
          '100%': {
            backgroundPosition: '200% 50%',
          },
        },
        '@keyframes slideIn': {
          '0%': {
            transform: 'translateY(20px)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateY(0)',
            opacity: 1,
          },
        },
        '@keyframes fadeIn': {
          '0%': {
            opacity: 0,
          },
          '100%': {
            opacity: 1,
          },
        },
        '@keyframes pulse': {
          '0%': {
            transform: 'scale(1)',
          },
          '50%': {
            transform: 'scale(1.05)',
          },
          '100%': {
            transform: 'scale(1)',
          },
        },
        body: {
          scrollbarColor: "#FFD700 #1A1A1A",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            width: 8,
            height: 8,
            backgroundColor: "#1A1A1A",
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: "#FFD700",
            border: "2px solid #1A1A1A",
          },
          "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
            backgroundColor: "#FFE44D",
          },
          background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)',
          [breakpoints.xs]: {
            fontSize: '14px',
          },
          [breakpoints.sm]: {
            fontSize: '15px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'uppercase',
          padding: '10px 24px',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.3), transparent)',
            transition: 'all 0.6s',
          },
          '&:hover': {
            transform: 'translateY(-2px)',
          },
          '&:hover::before': {
            left: '100%',
          },
          '&:active': {
            transform: 'translateY(1px)',
          },
          [breakpoints.xs]: {
            padding: '8px 16px',
            fontSize: '0.875rem',
          },
        },
        contained: {
          background: 'linear-gradient(45deg, #FFD700 30%, #FFC400 90%)',
          backgroundSize: '200% 100%',
          animation: 'shine 3s infinite linear',
          color: 'black',
          boxShadow: '0 4px 12px rgba(255, 215, 0, 0.2)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(255, 215, 0, 0.4)',
          },
        },
        outlined: {
          borderColor: '#FFD700',
          '&:hover': {
            borderColor: '#FFE44D',
            backgroundColor: 'rgba(255, 215, 0, 0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          backgroundImage: 'linear-gradient(45deg, rgba(26,26,26,0.95) 0%, rgba(26,26,26,0.95) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,215,0,0.1)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 48px rgba(0,0,0,0.6)',
            '&::after': {
              opacity: 1,
            },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            borderRadius: 'inherit',
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
            background: 'linear-gradient(45deg, transparent, rgba(255,215,0,0.1), transparent)',
            pointerEvents: 'none',
          },
          animation: 'slideIn 0.5s ease-out',
          [breakpoints.xs]: {
            margin: '8px',
            padding: '12px',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(90deg, rgba(26,26,26,0.95) 0%, rgba(26,26,26,0.95) 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,215,0,0.1)',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.3), transparent)',
          },
          [breakpoints.xs]: {
            '& .MuiToolbar-root': {
              minHeight: '56px',
              padding: '0 8px',
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(180deg, rgba(26,26,26,0.98) 0%, rgba(10,10,10,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(255,215,0,0.1)',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '1px',
            background: 'linear-gradient(180deg, transparent, rgba(255,215,0,0.3), transparent)',
          },
          [breakpoints.xs]: {
            width: '100% !important',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          margin: '4px 8px',
          borderRadius: 4,
          transition: 'all 0.3s ease-in-out',
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,215,0,0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255,215,0,0.25)',
            },
            '&::after': {
              opacity: 1,
              transform: 'scaleX(1)',
            },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 4,
            right: 4,
            height: '2px',
            background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
            opacity: 0,
            transform: 'scaleX(0)',
            transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
          },
          '&:hover::after': {
            opacity: 0.5,
            transform: 'scaleX(1)',
          },
          [breakpoints.xs]: {
            padding: '8px 16px',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#FFD700',
          minWidth: 40,
          transition: 'transform 0.2s ease-in-out',
          '.Mui-selected &': {
            transform: 'scale(1.1)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,215,0,0.1)',
          transition: 'background-color 0.2s ease-in-out',
          [breakpoints.xs]: {
            padding: '8px',
            '&:first-of-type': {
              paddingLeft: '16px',
            },
            '&:last-of-type': {
              paddingRight: '16px',
            },
          },
        },
        head: {
          fontWeight: 600,
          backgroundColor: 'rgba(26,26,26,0.95)',
          backgroundImage: 'linear-gradient(180deg, rgba(26,26,26,0.95) 0%, rgba(26,26,26,0.98) 100%)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          },
          [breakpoints.xs]: {
            height: '28px',
            fontSize: '0.75rem',
          },
        },
        filled: {
          backgroundImage: 'linear-gradient(45deg, var(--mui-palette-primary-dark) 0%, var(--mui-palette-primary-main) 100%)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(26,26,26,0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,215,0,0.1)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: 4,
        },
        arrow: {
          color: 'rgba(26,26,26,0.95)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'linear-gradient(45deg, rgba(26,26,26,0.98) 0%, rgba(26,26,26,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,215,0,0.1)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
          [breakpoints.xs]: {
            margin: '16px',
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: 'rgba(255,215,0,0.1)',
        },
        bar: {
          borderRadius: 4,
          backgroundImage: 'linear-gradient(45deg, #FFD700 30%, #FFC400 90%)',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          [breakpoints.xs]: {
            '& > .MuiGrid-item': {
              padding: '8px',
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          [breakpoints.xs]: {
            minWidth: 'auto',
            padding: '6px 12px',
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          zIndex: 1000,
          animation: 'pulse 2s infinite',
          [breakpoints.xs]: {
            width: '40px',
            height: '40px',
            minHeight: '40px',
          },
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme);

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { currentUser } = useFirebase();
  const userRole = currentUser?.displayName || 'dealership';
  
  // Redirect based on user role
  if (userRole === 'admin') {
    return <Navigate to="/dealership/dashboard" />;
  } else if (userRole === 'repair-shop') {
    return <Navigate to="/repair-shop/jobs" />;
  } else if (userRole === 'spare-shop') {
    return <Navigate to="/spare-shop/parts-search" />;
  } else {
    return <Navigate to="/dealership/dashboard" />;
  }
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FirebaseProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/spare-shop/parts-search" element={<ProtectedRoute><PartsSearch /></ProtectedRoute>} />
            <Route path="/spare-shop/stock" element={<ProtectedRoute><StockManagement /></ProtectedRoute>} />
            <Route path="/spare-shop/suppliers" element={<ProtectedRoute><SupplierManagement /></ProtectedRoute>} />
            <Route path="/spare-shop/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/dealership/dashboard" element={<ProtectedRoute><DealershipDashboard /></ProtectedRoute>} />
            <Route path="/dealership/transactions" element={<ProtectedRoute><TransactionManagement /></ProtectedRoute>} />
            <Route path="/dealership/inventory" element={<ProtectedRoute><InventoryStatus /></ProtectedRoute>} />
            <Route path="/dealership/suppliers" element={<ProtectedRoute><SupplierNetwork /></ProtectedRoute>} />
            <Route path="/dealership/orders" element={<ProtectedRoute><OrderTracking /></ProtectedRoute>} />
            <Route path="/dealership/service-history" element={<ProtectedRoute><DealershipServiceHistory /></ProtectedRoute>} />
            <Route path="/dealership/customers" element={<ProtectedRoute><CustomerManagement /></ProtectedRoute>} />
            <Route path="/dealership/reports" element={<ProtectedRoute><ReportsAnalytics /></ProtectedRoute>} />
            <Route path="/dealership/alerts" element={<ProtectedRoute><PartsAlertsDashboard /></ProtectedRoute>} />
            <Route path="/repair-shop/capacity" element={<ProtectedRoute><CapacityManagement /></ProtectedRoute>} />
            <Route path="/repair-shop/jobs" element={<ProtectedRoute><RepairJobsBoard /></ProtectedRoute>} />
            <Route path="/repair-shop/parts-order" element={<ProtectedRoute><PartsOrderInterface /></ProtectedRoute>} />
            <Route path="/repair-shop/schedule" element={<ProtectedRoute><ScheduleManagement /></ProtectedRoute>} />
            <Route path="/repair-shop/service-history" element={<ProtectedRoute><RepairShopServiceHistory /></ProtectedRoute>} />
            <Route path="/repair-shop/availability" element={<ProtectedRoute><AvailabilityCalendar /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfileManagement /></ProtectedRoute>} />
            <Route path="/messages" element={<ProtectedRoute><MessagesHub /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><NotificationsCenter /></ProtectedRoute>} />
            <Route path="/" element={<RoleBasedRedirect />} />
          </Routes>
        </Router>
      </FirebaseProvider>
    </ThemeProvider>
  );
}

export default App; 